import React from "react";
import PropTypes from "prop-types";
import { ArrowRightLeft, Ban, BellRing, CheckCheck, CirclePlus, ClipboardCheck, FileDown, Flag, History, Pencil, RotateCcw, Users } from "lucide-react";

import EmptyState from "./EmptyState";
import { LoadingBlock } from "./Loading";

const eventTypes = {
  created: { icon: CirclePlus, tone: "success" },
  checked: { icon: ClipboardCheck, tone: "success" },
  exported: { icon: FileDown, tone: "neutral" },
  rejected: { icon: RotateCcw, tone: "error" },
  returned: { icon: RotateCcw, tone: "error" },
  voided: { icon: Ban, tone: "error" },
  edited: { icon: Pencil, tone: "info" },
  handover: { icon: Users, tone: "info" },
  "part-due": { icon: BellRing, tone: "neutral" },
  "part-acknowledged": { icon: CheckCheck, tone: "info" },
  "part-confirmed": { icon: ClipboardCheck, tone: "success" },
  "part-activated": { icon: ArrowRightLeft, tone: "success" },
  "part-completed": { icon: Flag, tone: "success" },
};

const PackHistoryTimeline = ({ events = [], loading = false }) => {
  const groupedEvents = events.reduce((groups, event) => {
    const group = groups.find(({ date }) => date === event.dateLabel);
    if (group) group.events.push(event);
    else groups.push({ date: event.dateLabel, events: [event] });
    return groups;
  }, []);

  if (loading) return <section aria-labelledby="pack-history-title" className="wsm-pack-history"><div className="wsm-pack-history__heading"><h2 id="pack-history-title">History</h2><p>Track changes and actions taken on this pack.</p></div><LoadingBlock label="Loading pack history" lines={6} /></section>;

  return (
    <section aria-labelledby="pack-history-title" className="wsm-pack-history">
      <div className="wsm-pack-history__heading"><h2 id="pack-history-title">History</h2><p>Track changes and actions taken on this pack.</p></div>
      {events.length === 0 ? <EmptyState compact description="Actions taken on this pack will be recorded here." icon={History} title="No history yet" /> : (
        <div className="wsm-pack-history__groups">
          {groupedEvents.map((group, groupIndex) => (
            <section aria-labelledby={"history-group-" + groupIndex} className="wsm-pack-history__group" key={group.date}>
              <h3 id={"history-group-" + groupIndex}>{group.date}</h3>
              <ol className="wsm-pack-history__timeline">
                {group.events.map((event) => {
                  const config = eventTypes[event.type] || eventTypes.edited;
                  const Icon = config.icon;
                  return <li className="wsm-pack-history__event" key={event.id}>
                    <span className={"wsm-pack-history__icon wsm-pack-history__icon--" + config.tone}><Icon aria-hidden="true" /></span>
                    <div className="wsm-pack-history__content">
                      <div className="wsm-pack-history__event-header"><strong>{event.title}</strong><time>{event.time}</time></div>
                      {event.detail && <p>{event.detail}</p>}
                      {event.facts?.length > 0 && <dl className="wsm-pack-history__facts">
                        {event.facts.map((fact) => <div key={fact.label}><dt>{fact.label}</dt><dd>{fact.value}</dd></div>)}
                      </dl>}
                      <span className="wsm-pack-history__actor">{event.actorLabel || "by"} {event.user}</span>
                    </div>
                  </li>;
                })}
              </ol>
            </section>
          ))}
        </div>
      )}
    </section>
  );
};

PackHistoryTimeline.propTypes = {
  events: PropTypes.arrayOf(PropTypes.shape({
    actorLabel: PropTypes.string, dateLabel: PropTypes.string.isRequired, detail: PropTypes.string,
    facts: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string.isRequired, value: PropTypes.string.isRequired })),
    id: PropTypes.string.isRequired,
    time: PropTypes.string.isRequired, title: PropTypes.string.isRequired, type: PropTypes.string.isRequired, user: PropTypes.string.isRequired,
  })),
  loading: PropTypes.bool,
};

export default PackHistoryTimeline;